<template>
  <ModalForm
    :title="title"
    :show="props.show"
    @submit="submit"
    @close="close"
    :processing="form.processing"
    button="Add"
  >
    <div class="space-y-4">
      <div v-if="!inviting">
        <LabelErrorContainer label="Pilot" :error="form.errors['user.id']">
          <SelectSearch
            instant
            v-model="form.user"
            @search="search"
            :options="options"
            placeholderKey="name"
            v-slot="{ option, selected }"
          >
            <SelectListItemSimple
              :option="option"
              :selected="selected"
              primaryKey="name"
            />
          </SelectSearch>
        </LabelErrorContainer>
        <InputHelpText
          >Not listed?
          <LinkBase @click.prevent="inviting = true"
            >Invite a new pilot</LinkBase
          >
          by email.</InputHelpText
        >
      </div>

      <template v-else>
        <div>
          <InputGroup
            v-model="form.name"
            label="Name"
            :error="form.errors.name"
          />
        </div>
        <div>
          <InputGroup
            v-model="form.email"
            type="email"
            label="Email"
            :error="form.errors.email"
          />
          <InputHelpText
            >They'll get an email to set up their account and will see the
            flights they're on.
            <LinkBase @click.prevent="inviting = false"
              >Choose an existing pilot</LinkBase
            >
            instead.</InputHelpText
          >
        </div>
      </template>

      <div v-if="!props.presetLeg">
        <LabelErrorContainer label="Role" :error="form.errors.role">
          <SelectSimpleNative v-model="form.role" :error="form.errors.role">
            <option value="sic">SIC (second in command)</option>
            <option value="pic">PIC (captain)</option>
          </SelectSimpleNative>
        </LabelErrorContainer>
        <InputHelpText v-if="form.role === 'pic' && replacesPic"
          >The current PIC on those legs will become an SIC.</InputHelpText
        >
      </div>

      <div v-if="!props.presetLeg && props.legs.length > 1">
        <LabelErrorContainer
          label="Legs"
          :error="form.errors.legs || form.errors['legs.0']"
        >
          <LegCheckboxList v-model="form.legs" :legs="props.legs" />
        </LabelErrorContainer>
        <InputHelpText
          >Uncheck any legs this pilot won't be flying, for instance the return
          of a long trip.</InputHelpText
        >
      </div>
    </div>
  </ModalForm>
</template>

<script setup>
import ModalForm from '@/Common/Overlays/Modals/ModalForm.vue'
import LabelErrorContainer from '@/Common/Forms/Inputs/LabelErrorContainer.vue'
import InputGroup from '@/Common/Forms/Inputs/InputGroup.vue'
import InputHelpText from '@/Common/Forms/Inputs/InputHelpText.vue'
import SelectSearch from '@/Common/Forms/SelectMenus/SelectSearch.vue'
import SelectListItemSimple from '@/Common/Forms/SelectMenus/SelectListItemSimple.vue'
import SelectSimpleNative from '@/Common/Forms/SelectMenus/SelectSimpleNative.vue'
import LinkBase from '@/Common/Text/LinkBase.vue'
import LegCheckboxList from './LegCheckboxList.vue'
import { computed, ref, watch } from 'vue'
import { useForm } from '@inertiajs/inertia-vue3'
import { Inertia } from '@inertiajs/inertia'
import { legRoute, roleLabel } from '../Calendar/pilotColors'

const props = defineProps({
  show: Boolean,
  currentPilots: Array,
  legs: Array,
  trip: Object,
  // Set when opened from an empty slot in the crew grid: the pilot goes into
  // that one slot, so the role and leg choices are hidden
  presetLeg: Object,
  presetRole: String,
})

const title = computed(() =>
  props.presetLeg
    ? `Assign ${roleLabel(props.presetRole)} for ${legRoute(props.presetLeg)}`
    : 'Add Pilot'
)

const emit = defineEmits(['close'])

const inviting = ref(false)

const allLegIds = () => props.legs.map((leg) => leg.id)

function legHasPic(leg) {
  return (leg.pilots || []).some((pilot) => pilot.pivot?.role === 'pic')
}

const form = useForm({
  user: null,
  name: null,
  email: null,
  role: 'sic',
  legs: allLegIds(),
})

// Whether any chosen leg already has a captain
const replacesPic = computed(() =>
  props.legs.some((leg) => form.legs.includes(leg.id) && legHasPic(leg))
)

// The first pilot on a trip is normally the captain
watch(
  () => props.show,
  (show) => {
    if (!show) return

    if (props.presetLeg) {
      form.legs = [props.presetLeg.id]
      form.role = props.presetRole
      return
    }

    form.legs = allLegIds()
    form.role = props.legs.some(legHasPic) ? 'sic' : 'pic'
  }
)

watch(inviting, () => {
  form.user = null
  form.name = null
  form.email = null
  form.clearErrors()
})

function submit() {
  const url = props.presetLeg
    ? route('trips.legs.crew.store', {
        trip: props.trip.id,
        leg: props.presetLeg.id,
      })
    : route('trips.add-pilot', props.trip)

  form
    .transform((data) =>
      inviting.value
        ? { ...data, user: null }
        : { ...data, name: null, email: null }
    )
    .post(url, {
      preserveScroll: true,
      onSuccess: () => close(),
    })
}

function close() {
  emit('close')
  form.reset()
  form.clearErrors()
  inviting.value = false
  options.value = []
}

// Search. For a slot, only pilots already in that role on that leg are left out;
// for the trip, everyone already on it.
const options = ref([])

const exceptIds = computed(() => {
  if (props.presetLeg)
    return (props.presetLeg.pilots || [])
      .filter((pilot) => pilot.pivot?.role === props.presetRole)
      .map((pilot) => pilot.id)

  return props.currentPilots.map((el) => el.id)
})

let search = _.debounce(function (query) {
  axios
    .get(route('planes.users.search', props.trip.plane), {
      params: {
        query: query,
        except: exceptIds.value,
        type: 'pilots',
      },
    })
    .then((response) => {
      options.value = response.data
    })
    .catch(() => Inertia.reload())
}, 350)
</script>
